<template>
  <!-- 병원별/제품별 순위 위젯 (REQ-MOD-3). 탭형 정렬: 순위 | 아이콘 | 이름(ellipsis) | 공유 bar track | 값(우측 정렬). -->
  <div class="column-box ranking-widget">
    <div class="ranking-widget-header">
      <h3>{{ title }}</h3>
      <span v-if="headerNote" class="ranking-widget-note">{{ headerNote }}</span>
    </div>
    <DashboardWidgetState
      :loading="loading"
      :error="error"
      :is-empty="isEmpty"
      :empty-message="emptyMessage"
      @retry="$emit('retry')"
    >
      <div
        v-for="(item, index) in items"
        :key="item.key"
        class="ranking-row"
        :class="{ 'ranking-row-etc': item.isEtc }"
      >
        <div class="ranking-rank">{{ item.isEtc ? '-' : index + 1 }}</div>
        <div v-if="item.isEtc" class="ranking-avatar ranking-avatar-etc">
          <i class="fa-solid fa-ellipsis" />
        </div>
        <div v-else-if="item.imgUrl" class="ranking-avatar">
          <img :src="item.imgUrl" />
        </div>
        <div
          v-else
          class="ranking-avatar"
          :style="`background-color: ${hashColor(item.name)};`"
        >
          {{ trimFirstLetter(item.name) }}
        </div>
        <a-tooltip :title="item.name">
          <div class="ranking-name">{{ item.name }}</div>
        </a-tooltip>
        <div class="ranking-bar-track">
          <div
            class="ranking-bar-fill"
            :class="{ 'ranking-bar-fill-etc': item.isEtc }"
            :style="`width: ${barWidth(item.count)}%;`"
          />
        </div>
        <div class="ranking-value">
          {{ valueText(item) }}
        </div>
      </div>
    </DashboardWidgetState>
  </div>
</template>

<script>
import DashboardWidgetState from './DashboardWidgetState.vue';
import { colorCodes } from '@/utils/enums';
import { formatCommaNumber } from '@/utils/utils';

export default {
  name: 'RankingWidget',
  components: { DashboardWidgetState },
  emits: ['retry'],
  props: {
    title: { type: String, required: true },
    headerNote: { type: String, default: '' },
    // item: { key, name, count, percentage?, isEtc, hideValue?, imgUrl? }
    items: { type: Array, default: () => [] },
    showPercentage: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    isEmpty: { type: Boolean, default: false },
    emptyMessage: { type: String, default: '' },
  },
  computed: {
    maxCount() {
      const counts = this.items.filter((item) => !item.isEtc).map((item) => item.count);
      return counts.length ? Math.max(...counts) : 1;
    },
  },
  methods: {
    barWidth(count) {
      if (!this.maxCount) return 0;
      return Math.min(100, Math.round((count / this.maxCount) * 100));
    },
    trimFirstLetter(input) {
      if (!input) return '';
      return input.split('')[0];
    },
    // 이름 기반 결정적 색상 배정(렌더마다 바뀌지 않도록 해시 사용, Math.random 대체)
    hashColor(input) {
      const text = input || '';
      let hash = 0;
      for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) % colorCodes.length;
      }
      return colorCodes[Math.abs(hash) % colorCodes.length];
    },
    // 병원 그 외 행처럼 라벨에 이미 건수가 병기된 경우 값 칸 중복 표기를 생략한다.
    valueText(item) {
      if (item.hideValue) return '';
      const count = `${formatCommaNumber(item.count)}${this.$i18n.t('common_case')}`;
      if (this.showPercentage && item.percentage !== undefined) {
        return `${count} · ${Math.round(item.percentage)}%`;
      }
      return count;
    },
  },
};
</script>

<style scoped>
/* 부모 .dashboard-row--split(gap:16px)와 동일한 width-기반 수식을 사용한다.
   flex-grow/basis에 맡기지 않고 명시적 width로 고정해 다른 행(SummaryCards)과
   픽셀 단위로 경계를 맞춘다. */
.ranking-widget {
  width: calc(50% - 8px);
  flex: none;
  min-width: 0;
  box-sizing: border-box;
}

.ranking-widget-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;
}

.ranking-widget-header h3 {
  margin-bottom: 0;
}

.ranking-widget-note {
  font-size: 13px;
  color: #8d939a;
  white-space: nowrap;
}

/* 탭형 5열 그리드: 순위 | 아이콘(고정) | 이름(가변, ellipsis) | bar track(가변, 모든 행 동일 시작/끝 x) | 값(고정폭, 우측정렬) */
.ranking-row {
  display: grid;
  grid-template-columns: 20px 32px minmax(60px, 1fr) minmax(70px, 1fr) 116px;
  align-items: center;
  column-gap: 10px;
  padding: 9px 6px;
  border-radius: 8px;
}

.ranking-row + .ranking-row {
  margin-top: 2px;
}

.ranking-row-etc {
  background-color: #f3f5f6;
  border-top: 1px dashed rgba(0, 0, 0, 0.08);
  margin-top: 6px;
  padding-top: 10px;
}

.ranking-rank {
  font-size: 13px;
  font-weight: 600;
  color: #acb2b9;
  text-align: center;
}

/* 원래 순위 목록에서 쓰던 로고/이니셜 아바타 디자인 그대로(사이즈/라운드/색상 규칙 동일) */
.ranking-avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  flex-shrink: 0;
}

.ranking-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.ranking-avatar-etc {
  background-color: #dde1e4;
  color: #6e747c;
}

.ranking-name {
  font-size: 14px;
  color: #4b5158;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.ranking-row-etc .ranking-name {
  color: #6e747c;
  font-weight: 600;
}

.ranking-bar-track {
  height: 6px;
  border-radius: 3px;
  background-color: #f1f2f3;
  overflow: hidden;
}

.ranking-bar-fill {
  height: 100%;
  border-radius: 3px;
  background-color: #519eec;
}

.ranking-bar-fill-etc {
  background-color: #acb2b9;
}

.ranking-value {
  font-size: 14px;
  font-weight: bold;
  color: #4b5158;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}

/* ==========================================================================
   모바일 (767px 이하)

   이 파일에는 @media 가 하나도 없었다. 두 가지가 겹쳐 있었다.

   (1) 카드 자체가 width: calc(50% - 8px) 로 묶여 있다.
       PC 는 두 장을 나란히 놓으려는 것이지만, 폰에서 세로로 쌓아도 폭은 그대로라
       카드 오른쪽이 회색으로 비고 안쪽은 120px 남짓밖에 안 됐다.

   (2) 한 줄 5열 그리드가 요구하는 최소 폭이 350px 다.
       카드를 100% 로 넓혀도 안쪽은 307px 라 43px 이 모자라고,
       가변 열이 최소값으로 눌려 이름이 60px 로 찌그러진다.

   그래서 폰에서는 카드를 100% 로 펴고, 한 줄을 두 줄로 나눈다.

       1줄  [순위] [아이콘] [이름 ─────────────]
       2줄  [순위] [아이콘] [── 막대 ──] [41건]

   이름이 60px → 약 243px, 막대도 70px → 약 191px 로 오히려 넓어진다.
   건수 열의 116px 고정은 풀어 내용만큼만 쓰게 한다(빈 자리였다).

   배치는 클래스가 아니라 자식 순서로 지정한다. 이름이 <a-tooltip> 으로 감싸여 있어
   래퍼 DOM 이 생기면 .ranking-name 이 그리드 자식이 아니게 되는데,
   순서(순위·아이콘·이름·막대·건수)는 어느 쪽이든 같기 때문이다.

   PC 는 위의 5열 한 줄 정의를 그대로 쓴다.
   ========================================================================== */
@media screen and (max-width: 767px) {
  /* 카드를 가로 전체로 편다 */
  .ranking-widget {
    width: 100%;
  }

  .ranking-row {
    grid-template-columns: 20px 32px 1fr auto;
    column-gap: 6px;
    row-gap: 4px;
    padding: 9px 2px;
  }

  /* 순위 · 아이콘 — 두 줄에 걸쳐 세로 가운데 */
  .ranking-row > *:nth-child(1),
  .ranking-row > *:nth-child(2) {
    grid-row: 1 / 3;
    align-self: center;
  }

  /* 이름 — 첫 줄 전체 */
  .ranking-row > *:nth-child(3) {
    grid-column: 3 / 5;
    grid-row: 1;
  }

  /* 막대 — 둘째 줄 왼쪽 */
  .ranking-row > *:nth-child(4) {
    grid-column: 3;
    grid-row: 2;
  }

  /* 건수 — 둘째 줄 오른쪽. 116px 고정 대신 내용만큼 */
  .ranking-row > *:nth-child(5) {
    grid-column: 4;
    grid-row: 2;
  }
}
</style>
